/**
 * Kumanya (Inventory & Provision) metinleri (ADR 0099; `gemi-envanter-kumanya.jpeg`). Ekranlar
 * 2. aşamada; anahtarlar API sözleşmesinin değerleriyle (durum, depolama, neden kodu, hata kodu)
 * eşleşir. Ekran adları İngilizce (referans), Türkçe ikinci dil.
 */

export const provisionsEn = {
  title: 'Inventory & Provision',
  subtitle: 'Stock In / Out | Provision | Purchasing Integration',
  sections: {
    dashboard: 'Provision Dashboard',
    list: 'Provision List',
    lots: 'Batches & Expiry',
    movements: 'Recent Transactions',
    alerts: 'Alerts & Notifications',
    summary: 'Stock Summary',
    distribution: 'Stock Level Distribution',
    quickActions: 'Quick Actions',
    library: 'Starter Provision Library',
    settings: 'Provision Settings',
  },
  cards: {
    totalItems: 'Total Provision Items',
    low: 'Low Stock Items',
    critical: 'Critical Items',
    todayIn: 'Today Stock In',
    todayOut: 'Today Stock Out',
    expiring: 'Expiring Soon',
    expired: 'Expired Batches',
  },
  columns: {
    itemCode: 'Item Code',
    item: 'Item Name',
    specification: 'Specification',
    category: 'Category',
    subCategory: 'Subcategory',
    storage: 'Storage',
    temperature: 'Temperature',
    allergens: 'Allergens',
    unit: 'Unit',
    packSize: 'Pack Size',
    onHand: 'On Hand',
    min: 'Min. Level',
    reorder: 'Reorder Level',
    max: 'Max. Level',
    status: 'Status',
    location: 'Location',
    batch: 'Batch No.',
    expiry: 'Expiry Date',
    received: 'Received',
    daysToExpiry: 'Days Left',
    balance: 'Balance',
    nextExpiry: 'Next Expiry',
    costCenter: 'Cost Center',
    value: 'Value',
    date: 'Date',
    type: 'Type',
    qty: 'Qty',
    reason: 'Reason',
    user: 'User',
    actions: 'Actions',
  },
  filters: {
    search: 'Search item, code, IMPA, ISSA…',
    allCategories: 'All categories',
    allStorage: 'All storage types',
    allStatuses: 'All statuses',
    allergen: 'Allergen',
    stockOnly: 'Only items on board',
    expiringOnly: 'Expiring soon',
    expiredOnly: 'Expired',
  },
  status: { normal: 'Normal', low: 'Low', critical: 'Critical', notOnBoard: 'Not on board' },
  expiryState: {
    expired: 'Expired',
    expiring: 'Expiring soon',
    ok: 'OK',
    none: 'No expiry',
  },
  storageTypes: {
    Dry: 'Dry',
    Chilled: 'Chilled',
    Frozen: 'Frozen',
    Chemical: 'Chemical',
    Medical: 'Medical',
  },
  costCenters: {
    'Crew Provisions': 'Crew Provisions',
    'Hotel & Welfare': 'Hotel & Welfare',
    Medical: 'Medical',
    'Guest Provisions': 'Guest Provisions',
  },
  tracking: { Yes: 'Required', Optional: 'Optional', No: 'Not tracked' },
  allergens: {
    Gluten: 'Gluten',
    Crustaceans: 'Crustaceans',
    Egg: 'Egg',
    Fish: 'Fish',
    Peanut: 'Peanut',
    Soy: 'Soy',
    Milk: 'Milk',
    'Tree Nuts': 'Tree nuts',
    Celery: 'Celery',
    Mustard: 'Mustard',
    Sesame: 'Sesame',
    Sulphites: 'Sulphites',
    Lupin: 'Lupin',
    Molluscs: 'Molluscs',
  },
  kinds: {
    in: 'Stock In',
    receipt: 'Receipt (GRN)',
    out: 'Stock Out',
    adjust: 'Count adjustment',
    dispose: 'Disposal',
  },
  disposalReasons: {
    expired: 'Expired',
    damaged: 'Damaged',
    spoiled: 'Spoiled',
    temperature_breach: 'Temperature breach',
    recalled: 'Recalled by supplier',
    pest: 'Pest contamination',
    other: 'Other',
  },
  reasonCodes: { fefo_override: 'FEFO override' },
  lotSources: {
    goods_receipt_line: 'Goods receipt',
    manual: 'Manual stock in',
    opening: 'Opening stock',
  },
  actions: {
    stockIn: 'Add Stock In',
    stockOut: 'Add Stock Out',
    dispose: 'Dispose / Write off',
    count: 'Stock Count',
    request: 'Create Provision Request',
    viewRequests: 'View Purchasing Requests',
    applyLibrary: 'Apply starter library',
    previewLibrary: 'Preview',
    save: 'Save',
    cancel: 'Cancel',
  },
  forms: {
    quantity: 'Quantity',
    batchNo: 'Batch No.',
    expiryDate: 'Expiry date',
    receivedDate: 'Received date',
    lot: 'Batch',
    autoFefo: 'Automatic (FEFO: earliest expiry first)',
    fefoReason: 'Reason for not using the earliest-expiring batch',
    disposalReason: 'Reason',
    disposalNote: 'Details',
    counted: 'Counted',
    countReason: 'Count note',
    unitPrice: 'Unit price',
    currency: 'Currency',
    location: 'Location',
    expiryWarningDays: 'Expiry warning window (days)',
  },
  hints: {
    fefo: 'Batches are issued earliest expiry first. Choosing another batch needs a reason.',
    expiredBlocked:
      'Expired batches cannot be issued for consumption; dispose of them with a reason.',
    allergenStarter: 'Starter tag — verify against the product label.',
    allergenVerified: 'Verified against the product label.',
    dietary: 'Dietary tags are not certifications (halal, kosher, gluten-free) until verified.',
    medical:
      'Medical consumables do not replace the flag-state approved medical stores / medicine chest.',
    chemical: 'Follow the SDS, segregation rules and approved substances.',
    alcohol: 'Alcohol is not part of the library; manage it as a separate controlled catalogue.',
    library:
      'Applies the product’s provision library ({{items}} items, {{categories}} categories). Items are matched by Item Code; only empty fields are filled, existing values and vessel stock are never changed.',
    libraryUpdateShips:
      'Apply the library after the vessels have been updated to this version: older vessel software does not know the new units.',
    officeReadOnly: 'Provision stock is vessel data; the office sees it read-only.',
  },
  library: {
    version: 'Library version',
    created: '{{count}} new items',
    adopted: '{{count}} existing items linked to their Item Code',
    filled: '{{count}} items with empty fields filled',
    unchanged: '{{count}} items unchanged',
    skipped: '{{count}} items skipped (name used by another item)',
    differs: '{{items}} items have values that differ from the library (kept)',
    categories: '{{count}} new categories',
    conflict: '{{name}} ({{itemCode}}): an item with the same name exists as {{type}}',
    nothing: 'Nothing to change — the library is already applied.',
  },
  alerts: {
    expired: '{{name}}: batch {{batch}} expired on {{date}} ({{quantity}} {{unit}})',
    expiring: '{{name}}: batch {{batch}} expires on {{date}} ({{days}} days)',
    critical: '{{name}} stock is critical ({{quantity}} {{unit}})',
    low: '{{name}} stock is below minimum level ({{quantity}} {{unit}})',
    empty: 'No alerts.',
  },
  empty: {
    list: 'No provision items match the filters.',
    lots: 'No batches on board.',
    movements: 'No transactions yet.',
    library: 'The provision catalogue is empty. Apply the starter library to begin.',
  },
  lotState: { ok: 'Valid', expiring: 'Expiring', expired: 'Expired', none: 'No expiry' },
  screen: {
    head: 'Inventory & Provision',
    sections: 'Inventory sections',
    purchasingIntegration: 'Purchasing Integration',
    vessel: 'Vessel',
    flag: 'Flag',
    port: 'Port',
    date: 'Date',
    newRecord: 'New Record',
    summary: 'Provision summary',
    tree: 'Provision Tree',
    treeSearch: 'Search in provisions...',
    allProvisions: 'All Provisions',
    expand: 'Expand {{name}}',
    collapse: 'Collapse {{name}}',
    categoryTabs: 'Provision categories',
    allItems: 'All Items',
    filters: 'Provision list filters',
    all: 'All',
    anyAllergen: 'Any allergen',
    anyExpiry: 'Any expiry',
    searchItem: 'Search item...',
    search: 'Search',
    openItem: 'Open {{name}}',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
    pagination: 'Provision list pages',
    pageSize: 'Rows per page',
    perPage: '{{size}} / page',
    emptyTitle: 'No provisions on board yet',
    emptyShip:
      'Record the first stock in with “New Record”, or clear “Only items on board” to see the catalogue.',
    emptyWeb:
      'The vessel has not recorded provision stock yet. Clear “Only items on board” to see the catalogue.',
    byCategory: 'By Category',
    byStorage: 'By Storage',
    items: 'Items',
    valueIn: 'Value ({{currency}})',
    totalRow: 'Total',
    noStorage: 'Not set',
    noSummary: 'No provision stock on board.',
    summaryTable: 'Stock summary table',
    refresh: 'Refresh',
    viewRequests: 'View All Requests',
    requestsTable: 'Open provision requests',
    requestsUnavailable:
      'Open provision requests appear here when you can see purchasing requests.',
    noRequests: 'No open provision requests.',
    requestNo: 'Request No.',
    requiredDate: 'Required',
    lines: 'Items',
    recentTable: 'Recent provision transactions',
    viewAll: 'View All',
    error: 'Provisions could not be loaded.',
    retry: 'Try again',
    closeNotice: 'Close message',
  },
  detail: {
    close: 'Close item details',
    inactive: 'Inactive',
    editLevels: 'Edit levels & location',
    stock: 'Stock on board',
    info: 'Item information',
    batchTracking: 'Batch tracking',
    expiryTracking: 'Expiry tracking',
    fefo: 'FEFO required',
    yes: 'Yes',
    no: 'No',
    shelfLife: 'Shelf life',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    impaIssa: 'IMPA / ISSA',
    allergensUnknown: 'No allergen tags — unknown, not “allergen free”. Check the product label.',
    allergensNone: 'No allergens.',
    dietary: 'Dietary tags',
    notOnBoard: 'Not stocked on this vessel.',
    lotsTable: 'Batches of the item',
    warningWindow_one: 'Expiry warning {{count}} day',
    warningWindow_other: 'Expiry warning {{count}} days',
    history: 'Transaction history',
    historyTable: 'Transactions of the item',
    loadError: 'Batches and history could not be loaded.',
  },
  dialogs: {
    close: 'Close',
    item: 'Item',
    noItem: 'No item found',
    receiptTitle: 'Add Stock In',
    issueTitle: 'Add Stock Out',
    disposalTitle: 'Dispose / write off: {{name}}',
    countTitle: 'Stock count: {{name}}',
    levelsTitle: 'Levels & location: {{name}}',
    trackingHint: 'Batch: {{batch}} · Expiry: {{expiry}} · On hand {{onHand}}',
    onHand: 'On hand {{onHand}}',
    newOnBoard: 'The item is not stocked on board yet: it is added with this location.',
    note: 'Note',
    lotImmutable:
      'A batch cannot be edited later; a wrong entry is corrected with a count adjustment and a new batch.',
    saveReceipt: 'Save Stock In',
    saveIssue: 'Save Stock Out',
    saveDisposal: 'Dispose',
    saveCount: 'Save count',
    lotOption: '{{batch}} · {{expiry}} · {{quantity}}',
    noBatch: 'No batch no.',
    fefoTitle: 'FEFO suggestion',
    fefoLot: 'Batch {{batch}} · expires {{expiry}}',
    unassigned: 'Stock without batch (before batch tracking)',
    shortage: 'Not enough usable stock: {{quantity}} missing (expired batches are not counted).',
    fefoOverride: 'An earlier-expiring batch is available. Give the reason for using this batch.',
    fefoOverrideFree: 'An earlier-expiring batch is available.',
    expiredLots_one: '{{count}} expired batch is not offered.',
    expiredLots_other: '{{count}} expired batches are not offered.',
    disposalHint: 'Disposal is recorded with its reason and cannot be edited later.',
    difference: 'Difference',
    countedFor: 'Counted for {{lot}}',
    levelsHint:
      'Status: Critical at zero or below half the minimum; Low at or below the minimum or the reorder level.',
  },
  notices: {
    received: 'Stock in saved: {{name}}, {{quantity}}.',
    issued_one: 'Stock out saved: {{name}}, {{quantity}}.',
    issued_other: 'Stock out saved: {{name}}, {{quantity}} from {{count}} batches (FEFO).',
    disposed: 'Disposal saved: {{name}}, {{quantity}}.',
    counted_one: 'Count saved: {{name}}, {{count}} adjustment.',
    counted_other: 'Count saved: {{name}}, {{count}} adjustments.',
    countedSame: 'Count saved: {{name}} matches the stock, no adjustment.',
    levels: 'Levels saved: {{name}}.',
  },
  formErrors: {
    required: 'Required.',
    positive: 'Enter a quantity greater than zero.',
    tooLarge: 'The quantity is too large.',
    tooLong: 'The text is too long.',
    future: 'The date cannot be in the future.',
    expiryRequired: 'This item requires an expiry date.',
    batchRequired: 'This item requires a batch number.',
    expired: 'The expiry date is before the received date: expired goods are not received.',
    lotBalance: 'More than the batch balance.',
    fefoReason: 'Give the reason for not using the earliest-expiring batch.',
    lotRequired: 'Choose a batch.',
    reasonRequired: 'A reason is required.',
    counted: 'Enter the counted quantity (0 or more).',
    notNegative: 'Cannot be negative.',
    maxBelowMin: 'The maximum cannot be below the minimum.',
  },
  office: {
    items: 'Items',
    preview: 'Library preview by category',
    previewTitle: 'Apply the starter library?',
    dryRun: 'Preview — nothing has been written yet. Confirm to apply.',
    confirmApply: 'Apply library',
    appliedTitle: 'Starter library applied.',
    conflicts: 'Skipped items',
    libraryForbidden: 'Applying the library needs the Material Master create and edit rights.',
    warningHint: 'Batches expiring within this window are flagged (1–365 days).',
    settingsSaved: 'The warning window is saved; vessels receive it with the next synchronisation.',
    settingsForbidden: 'Changing the warning window needs the settings edit right.',
    close: 'Close',
  },
  errors: {
    FORBIDDEN: 'You are not authorised for this provision action.',
    EXPIRY_REQUIRED: 'This item requires an expiry date on receipt.',
    BATCH_REQUIRED: 'This item requires a batch number on receipt.',
    VALIDATION_FAILED: 'Some values are not valid. Check the form.',
    NOT_FOUND: 'The record was not found; refresh the screen.',
    ROW_VERSION_CONFLICT: 'The record was changed by someone else; refresh and try again.',
    NETWORK: 'The server could not be reached.',
    LOT_EXPIRED: 'The batch has expired. It cannot be received or issued; dispose of it instead.',
    LOT_REQUIRED: 'This item is batch-tracked: choose a batch.',
    LOT_MISMATCH: 'The batch does not belong to this item on this vessel.',
    LOT_UNKNOWN: 'The batch was not found.',
    FEFO_REASON_REQUIRED: 'An earlier-expiring batch is available. Give a reason to use this one.',
    NEGATIVE_STOCK: 'Not enough stock in this batch.',
    NOT_PROVISION: 'The item is not a provision item.',
    MATERIAL_INACTIVE: 'The item is inactive.',
    ITEM_CODE_FIXED: 'The Item Code cannot be changed once set.',
    DUPLICATE_ITEM_CODE: 'Another item already uses this Item Code.',
    INVALID_DISPOSAL_REASON: 'Choose a disposal reason.',
    REASON_REQUIRED: 'A reason is required.',
    COUNT_REQUIRED: 'Enter at least one counted quantity.',
    DUPLICATE_COUNT: 'Each batch can be counted once.',
    INVALID_PROVISION_SETTINGS: 'The warning window must be between 1 and 365 days.',
    FUTURE_DATE: 'The date cannot be in the future.',
    ZERO_QUANTITY: 'Enter a quantity greater than zero.',
  },
};

export const provisionsTr: typeof provisionsEn = {
  title: 'Envanter ve Kumanya',
  subtitle: 'Stok Giriş / Çıkış | Kumanya | Satın Alma Bağlantısı',
  sections: {
    dashboard: 'Kumanya Panosu',
    list: 'Kumanya Listesi',
    lots: 'Partiler ve Son Kullanma',
    movements: 'Son Hareketler',
    alerts: 'Uyarılar ve Bildirimler',
    summary: 'Stok Özeti',
    distribution: 'Stok Seviyesi Dağılımı',
    quickActions: 'Hızlı İşlemler',
    library: 'Başlangıç Kumanya Kütüphanesi',
    settings: 'Kumanya Ayarları',
  },
  cards: {
    totalItems: 'Toplam Kumanya Kalemi',
    low: 'Düşük Stok',
    critical: 'Kritik Kalemler',
    todayIn: 'Bugün Giriş',
    todayOut: 'Bugün Çıkış',
    expiring: 'Son Kullanması Yaklaşan',
    expired: 'Süresi Geçmiş Parti',
  },
  columns: {
    itemCode: 'Kalem Kodu',
    item: 'Kalem Adı',
    specification: 'Özellik',
    category: 'Kategori',
    subCategory: 'Alt Kategori',
    storage: 'Depolama',
    temperature: 'Sıcaklık',
    allergens: 'Alerjenler',
    unit: 'Birim',
    packSize: 'Ambalaj',
    onHand: 'Eldeki',
    min: 'En Az',
    reorder: 'Sipariş Seviyesi',
    max: 'En Çok',
    status: 'Durum',
    location: 'Yer',
    batch: 'Parti No.',
    expiry: 'Son Kullanma',
    received: 'Kabul',
    daysToExpiry: 'Kalan Gün',
    balance: 'Bakiye',
    nextExpiry: 'En Yakın Son Kullanma',
    costCenter: 'Maliyet Merkezi',
    value: 'Değer',
    date: 'Tarih',
    type: 'Tür',
    qty: 'Miktar',
    reason: 'Gerekçe',
    user: 'Kullanıcı',
    actions: 'İşlemler',
  },
  filters: {
    search: 'Kalem, kod, IMPA, ISSA ara…',
    allCategories: 'Bütün kategoriler',
    allStorage: 'Bütün depolama türleri',
    allStatuses: 'Bütün durumlar',
    allergen: 'Alerjen',
    stockOnly: 'Yalnız gemide olanlar',
    expiringOnly: 'Son kullanması yaklaşan',
    expiredOnly: 'Süresi geçmiş',
  },
  status: { normal: 'Normal', low: 'Düşük', critical: 'Kritik', notOnBoard: 'Gemide yok' },
  expiryState: {
    expired: 'Süresi geçmiş',
    expiring: 'Yaklaşıyor',
    ok: 'Uygun',
    none: 'Son kullanmasız',
  },
  storageTypes: {
    Dry: 'Kuru',
    Chilled: 'Soğuk',
    Frozen: 'Donuk',
    Chemical: 'Kimyasal',
    Medical: 'Tıbbi',
  },
  costCenters: {
    'Crew Provisions': 'Mürettebat kumanyası',
    'Hotel & Welfare': 'Otel ve refah',
    Medical: 'Tıbbi',
    'Guest Provisions': 'Misafir kumanyası',
  },
  tracking: { Yes: 'Zorunlu', Optional: 'İsteğe bağlı', No: 'İzlenmiyor' },
  allergens: {
    Gluten: 'Gluten',
    Crustaceans: 'Kabuklular',
    Egg: 'Yumurta',
    Fish: 'Balık',
    Peanut: 'Yer fıstığı',
    Soy: 'Soya',
    Milk: 'Süt',
    'Tree Nuts': 'Sert kabuklu yemişler',
    Celery: 'Kereviz',
    Mustard: 'Hardal',
    Sesame: 'Susam',
    Sulphites: 'Sülfitler',
    Lupin: 'Acı bakla',
    Molluscs: 'Yumuşakçalar',
  },
  kinds: {
    in: 'Giriş',
    receipt: 'Mal kabulü (GRN)',
    out: 'Çıkış',
    adjust: 'Sayım düzeltmesi',
    dispose: 'İmha / zayi',
  },
  disposalReasons: {
    expired: 'Süresi geçti',
    damaged: 'Hasarlı',
    spoiled: 'Bozuldu',
    temperature_breach: 'Soğuk zincir bozuldu',
    recalled: 'Tedarikçi geri çağırdı',
    pest: 'Haşere bulaşması',
    other: 'Diğer',
  },
  reasonCodes: { fefo_override: 'FEFO dışı seçim' },
  lotSources: {
    goods_receipt_line: 'Mal kabulü',
    manual: 'Elle giriş',
    opening: 'Açılış stoğu',
  },
  actions: {
    stockIn: 'Stok Girişi',
    stockOut: 'Stok Çıkışı',
    dispose: 'İmha / Zayi',
    count: 'Sayım',
    request: 'Kumanya Talebi Aç',
    viewRequests: 'Satın Alma Taleplerini Gör',
    applyLibrary: 'Başlangıç kütüphanesini uygula',
    previewLibrary: 'Önizle',
    save: 'Kaydet',
    cancel: 'Vazgeç',
  },
  forms: {
    quantity: 'Miktar',
    batchNo: 'Parti No.',
    expiryDate: 'Son kullanma tarihi',
    receivedDate: 'Kabul tarihi',
    lot: 'Parti',
    autoFefo: 'Otomatik (FEFO: en erken son kullanma önce)',
    fefoReason: 'En erken son kullanmalı parti kullanılmıyorsa gerekçe',
    disposalReason: 'Neden',
    disposalNote: 'Açıklama',
    counted: 'Sayılan',
    countReason: 'Sayım notu',
    unitPrice: 'Birim fiyat',
    currency: 'Para birimi',
    location: 'Yer',
    expiryWarningDays: 'Son kullanma uyarı penceresi (gün)',
  },
  hints: {
    fefo: 'Partiler en erken son kullanmadan çıkar. Başka parti seçmek gerekçe ister.',
    expiredBlocked: 'Süresi geçmiş parti tüketime çıkamaz; gerekçeyle imha edilir.',
    allergenStarter: 'Başlangıç etiketi — ürün etiketiyle doğrulayın.',
    allergenVerified: 'Ürün etiketiyle doğrulandı.',
    dietary: 'Diyet etiketleri doğrulanana kadar sertifika (helal, koşer, glütensiz) sayılmaz.',
    medical: 'Tıbbi sarf, bayrak devletinin onaylı ilaç dolabının yerini tutmaz.',
    chemical: 'SDS, ayrıştırma kuralları ve onaylı maddelere uyun.',
    alcohol: 'Alkol kütüphanede yoktur; ayrı kontrollü katalog olarak yönetilir.',
    library:
      'Ürünün kumanya kütüphanesini uygular ({{items}} kalem, {{categories}} kategori). Kalemler Item Code ile eşleşir; yalnız boş alanlar doldurulur, var olan değerler ve gemi stoğu değişmez.',
    libraryUpdateShips:
      'Kütüphaneyi gemiler bu sürüme güncellendikten sonra uygulayın: eski gemi yazılımı yeni birimleri tanımaz.',
    officeReadOnly: 'Kumanya stoğu gemi verisidir; ofis salt okunur görür.',
  },
  library: {
    version: 'Kütüphane sürümü',
    created: '{{count}} yeni kalem',
    adopted: '{{count}} var olan kalem Item Code ile bağlandı',
    filled: '{{count}} kalemin boş alanları dolduruldu',
    unchanged: '{{count}} kalem aynı',
    skipped: '{{count}} kalem atlandı (ad başka kalemde)',
    differs: '{{items}} kalemde kütüphaneden farklı değer var (korundu)',
    categories: '{{count}} yeni kategori',
    conflict: '{{name}} ({{itemCode}}): aynı adlı kalem {{type}} türünde var',
    nothing: 'Değişecek bir şey yok — kütüphane zaten uygulanmış.',
  },
  alerts: {
    expired:
      '{{name}}: {{batch}} partisinin süresi {{date}} tarihinde geçti ({{quantity}} {{unit}})',
    expiring: '{{name}}: {{batch}} partisinin son kullanması {{date}} ({{days}} gün)',
    critical: '{{name}} stoğu kritik ({{quantity}} {{unit}})',
    low: '{{name}} stoğu en az seviyenin altında ({{quantity}} {{unit}})',
    empty: 'Uyarı yok.',
  },
  empty: {
    list: 'Süzgeçlere uyan kumanya kalemi yok.',
    lots: 'Gemide parti yok.',
    movements: 'Henüz hareket yok.',
    library: 'Kumanya kataloğu boş. Başlamak için başlangıç kütüphanesini uygulayın.',
  },
  lotState: {
    ok: 'Geçerli',
    expiring: 'Yaklaşıyor',
    expired: 'Süresi geçmiş',
    none: 'Son kullanmasız',
  },
  screen: {
    head: 'Envanter ve Kumanya',
    sections: 'Envanter bölümleri',
    purchasingIntegration: 'Satın Alma Bağlantısı',
    vessel: 'Gemi',
    flag: 'Bayrak',
    port: 'Liman',
    date: 'Tarih',
    newRecord: 'Yeni Kayıt',
    summary: 'Kumanya özeti',
    tree: 'Kumanya Ağacı',
    treeSearch: 'Kumanyada ara...',
    allProvisions: 'Bütün Kumanya',
    expand: '{{name}} aç',
    collapse: '{{name}} kapat',
    categoryTabs: 'Kumanya kategorileri',
    allItems: 'Bütün Kalemler',
    filters: 'Kumanya listesi süzgeçleri',
    all: 'Tümü',
    anyAllergen: 'Bütün alerjenler',
    anyExpiry: 'Bütün son kullanmalar',
    searchItem: 'Kalem ara...',
    search: 'Ara',
    openItem: '{{name}} aç',
    showing: '{{total}} kalemden {{from}}–{{to}}',
    pagination: 'Kumanya listesi sayfaları',
    pageSize: 'Sayfa başına satır',
    perPage: '{{size}} / sayfa',
    emptyTitle: 'Gemide henüz kumanya yok',
    emptyShip:
      'İlk girişi “Yeni Kayıt” ile yapın ya da kataloğu görmek için “Yalnız gemide olanlar”ı kaldırın.',
    emptyWeb:
      'Gemi henüz kumanya stoğu kaydetmedi. Kataloğu görmek için “Yalnız gemide olanlar”ı kaldırın.',
    byCategory: 'Kategoriye Göre',
    byStorage: 'Depolamaya Göre',
    items: 'Kalem',
    valueIn: 'Değer ({{currency}})',
    totalRow: 'Toplam',
    noStorage: 'Belirtilmemiş',
    noSummary: 'Gemide kumanya stoğu yok.',
    summaryTable: 'Stok özeti tablosu',
    refresh: 'Yenile',
    viewRequests: 'Bütün Talepler',
    requestsTable: 'Açık kumanya talepleri',
    requestsUnavailable:
      'Satın alma taleplerini görebiliyorsanız açık kumanya talepleri burada görünür.',
    noRequests: 'Açık kumanya talebi yok.',
    requestNo: 'Talep No.',
    requiredDate: 'İstenen',
    lines: 'Kalem',
    recentTable: 'Son kumanya hareketleri',
    viewAll: 'Tümü',
    error: 'Kumanya yüklenemedi.',
    retry: 'Yeniden dene',
    closeNotice: 'İletiyi kapat',
  },
  detail: {
    close: 'Kalem ayrıntısını kapat',
    inactive: 'Pasif',
    editLevels: 'Seviye ve yer',
    stock: 'Gemideki stok',
    info: 'Kalem bilgileri',
    batchTracking: 'Parti izleme',
    expiryTracking: 'Son kullanma izleme',
    fefo: 'FEFO zorunlu',
    yes: 'Evet',
    no: 'Hayır',
    shelfLife: 'Raf ömrü',
    days_one: '{{count}} gün',
    days_other: '{{count}} gün',
    impaIssa: 'IMPA / ISSA',
    allergensUnknown: 'Alerjen etiketi yok — bilinmiyor, “alerjensiz” değil. Ürün etiketine bakın.',
    allergensNone: 'Alerjen yok.',
    dietary: 'Diyet etiketleri',
    notOnBoard: 'Bu gemide stokta değil.',
    lotsTable: 'Kalemin partileri',
    warningWindow_one: 'Son kullanma uyarısı {{count}} gün',
    warningWindow_other: 'Son kullanma uyarısı {{count}} gün',
    history: 'Hareket geçmişi',
    historyTable: 'Kalemin hareketleri',
    loadError: 'Partiler ve geçmiş yüklenemedi.',
  },
  dialogs: {
    close: 'Kapat',
    item: 'Kalem',
    noItem: 'Kalem bulunamadı',
    receiptTitle: 'Stok Girişi',
    issueTitle: 'Stok Çıkışı',
    disposalTitle: 'İmha / zayi: {{name}}',
    countTitle: 'Sayım: {{name}}',
    levelsTitle: 'Seviye ve yer: {{name}}',
    trackingHint: 'Parti: {{batch}} · Son kullanma: {{expiry}} · Eldeki {{onHand}}',
    onHand: 'Eldeki {{onHand}}',
    newOnBoard: 'Kalem gemide henüz stokta değil: bu yerle eklenir.',
    note: 'Not',
    lotImmutable:
      'Parti sonradan düzeltilemez; yanlış giriş sayım düzeltmesi ve yeni partiyle düzeltilir.',
    saveReceipt: 'Girişi kaydet',
    saveIssue: 'Çıkışı kaydet',
    saveDisposal: 'İmha et',
    saveCount: 'Sayımı kaydet',
    lotOption: '{{batch}} · {{expiry}} · {{quantity}}',
    noBatch: 'Parti no. yok',
    fefoTitle: 'FEFO önerisi',
    fefoLot: '{{batch}} partisi · son kullanma {{expiry}}',
    unassigned: 'Partisiz stok (parti izlemeden önce)',
    shortage: 'Kullanılabilir stok yetmiyor: {{quantity}} eksik (süresi geçmiş partiler sayılmaz).',
    fefoOverride: 'Son kullanması daha erken bir parti var. Bu partiyi kullanma gerekçesini yazın.',
    fefoOverrideFree: 'Son kullanması daha erken bir parti var.',
    expiredLots_one: 'Süresi geçmiş {{count}} parti listede yok.',
    expiredLots_other: 'Süresi geçmiş {{count}} parti listede yok.',
    disposalHint: 'İmha gerekçesiyle kaydedilir ve sonradan değiştirilemez.',
    difference: 'Fark',
    countedFor: '{{lot}} için sayılan',
    levelsHint:
      'Durum: sıfırda ya da en azın yarısının altında Kritik; en az ya da sipariş seviyesinde veya altında Düşük.',
  },
  notices: {
    received: 'Giriş kaydedildi: {{name}}, {{quantity}}.',
    issued_one: 'Çıkış kaydedildi: {{name}}, {{quantity}}.',
    issued_other: 'Çıkış kaydedildi: {{name}}, {{quantity}}, {{count}} partiden (FEFO).',
    disposed: 'İmha kaydedildi: {{name}}, {{quantity}}.',
    counted_one: 'Sayım kaydedildi: {{name}}, {{count}} düzeltme.',
    counted_other: 'Sayım kaydedildi: {{name}}, {{count}} düzeltme.',
    countedSame: 'Sayım kaydedildi: {{name}} stokla aynı, düzeltme yok.',
    levels: 'Seviyeler kaydedildi: {{name}}.',
  },
  formErrors: {
    required: 'Zorunlu.',
    positive: 'Sıfırdan büyük bir miktar girin.',
    tooLarge: 'Miktar çok büyük.',
    tooLong: 'Metin çok uzun.',
    future: 'Tarih gelecekte olamaz.',
    expiryRequired: 'Bu kalem son kullanma tarihi ister.',
    batchRequired: 'Bu kalem parti numarası ister.',
    expired: 'Son kullanma kabul tarihinden önce: süresi geçmiş mal kabul edilmez.',
    lotBalance: 'Parti bakiyesinden fazla.',
    fefoReason: 'En erken son kullanmalı partiyi kullanmama gerekçesini yazın.',
    lotRequired: 'Parti seçin.',
    reasonRequired: 'Gerekçe zorunlu.',
    counted: 'Sayılan miktarı girin (0 ya da fazla).',
    notNegative: 'Eksi olamaz.',
    maxBelowMin: 'En çok, en azdan küçük olamaz.',
  },
  office: {
    items: 'Kalem',
    preview: 'Kategoriye göre kütüphane önizlemesi',
    previewTitle: 'Başlangıç kütüphanesi uygulansın mı?',
    dryRun: 'Önizleme — henüz hiçbir şey yazılmadı. Uygulamak için onaylayın.',
    confirmApply: 'Kütüphaneyi uygula',
    appliedTitle: 'Başlangıç kütüphanesi uygulandı.',
    conflicts: 'Atlanan kalemler',
    libraryForbidden: 'Kütüphaneyi uygulamak Material Master açma ve düzenleme yetkisi ister.',
    warningHint: 'Bu pencere içinde son kullanması dolan partiler işaretlenir (1–365 gün).',
    settingsSaved: 'Uyarı penceresi kaydedildi; gemiler bir sonraki senkronda alır.',
    settingsForbidden: 'Uyarı penceresini değiştirmek ayar düzenleme yetkisi ister.',
    close: 'Kapat',
  },
  errors: {
    FORBIDDEN: 'Bu kumanya işlemi için yetkiniz yok.',
    EXPIRY_REQUIRED: 'Bu kalemin kabulünde son kullanma tarihi zorunlu.',
    BATCH_REQUIRED: 'Bu kalemin kabulünde parti numarası zorunlu.',
    VALIDATION_FAILED: 'Bazı değerler geçersiz. Formu kontrol edin.',
    NOT_FOUND: 'Kayıt bulunamadı; ekranı yenileyin.',
    ROW_VERSION_CONFLICT: 'Kayıt başkası tarafından değiştirildi; yenileyip yeniden deneyin.',
    NETWORK: 'Sunucuya ulaşılamadı.',
    LOT_EXPIRED: 'Partinin süresi geçmiş. Kabul edilemez ve tüketime çıkamaz; imha edin.',
    LOT_REQUIRED: 'Bu kalem parti izlemeli: parti seçin.',
    LOT_MISMATCH: 'Parti bu gemideki bu kaleme ait değil.',
    LOT_UNKNOWN: 'Parti bulunamadı.',
    FEFO_REASON_REQUIRED:
      'Son kullanması daha erken bir parti var. Bunu kullanmak için gerekçe yazın.',
    NEGATIVE_STOCK: 'Bu partide yeterli stok yok.',
    NOT_PROVISION: 'Kalem kumanya kalemi değil.',
    MATERIAL_INACTIVE: 'Kalem pasif.',
    ITEM_CODE_FIXED: 'Item Code bir kez yazılınca değiştirilemez.',
    DUPLICATE_ITEM_CODE: 'Bu Item Code başka bir kalemde kullanılıyor.',
    INVALID_DISPOSAL_REASON: 'İmha nedenini seçin.',
    REASON_REQUIRED: 'Gerekçe zorunlu.',
    COUNT_REQUIRED: 'En az bir sayılan miktar girin.',
    DUPLICATE_COUNT: 'Her parti bir kez sayılır.',
    INVALID_PROVISION_SETTINGS: 'Uyarı penceresi 1 ile 365 gün arasında olmalı.',
    FUTURE_DATE: 'Tarih gelecekte olamaz.',
    ZERO_QUANTITY: 'Sıfırdan büyük bir miktar girin.',
  },
};
